import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, ArrowLeft, MapPin, Calendar, DollarSign, Phone, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ModernLogo } from "@/components/ModernLogo";

interface PaymentDetails {
  id: string;
  job_cost: number;
  customer_fee: number;
  total_charge: number;
  status: string;
  job: {
    title: string;
    description: string;
    location: string;
    required_date?: string;
  } | null;
  tradesman: {
    full_name: string;
    company_name?: string;
    email: string;
    phone?: string;
  } | null;
}

export const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      fetchPaymentDetails();
    }
  }, [sessionId]);

  const fetchPaymentDetails = async () => {
    try {
      // For the PaymentSuccess page, we need direct access to show payment confirmation
      // This is acceptable since the user is returning from Stripe with a valid session_id
      // and we only show limited payment information (no sensitive Stripe data)
      const { data: paymentData, error: paymentError } = await supabase
        .from("payments")
        .select(`
          id,
          job_cost,
          customer_fee,
          total_charge,
          status,
          job:jobs!payments_job_id_fkey(
            title,
            description,
            location,
            required_date
          ),
          tradesman:profiles!payments_tradesman_id_fkey(
            full_name,
            company_name,
            email,
            phone
          )
        `)
        .eq("stripe_session_id", sessionId)
        .maybeSingle();

      if (paymentError) throw paymentError;
      if (paymentData) setPaymentDetails(paymentData as unknown as PaymentDetails);
    } catch (error: any) {
      toast({
        title: "Error loading payment details",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">Invalid payment session</p>
            <Button asChild className="mt-4">
              <Link to="/">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Return Home
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading payment details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!paymentDetails) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">Payment details not found</p>
            <Button asChild className="mt-4">
              <Link to="/">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Return Home
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <ModernLogo size="sm" showTagline={false} />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Success Message */}
          <Card className="text-center shadow-elegant">
            <CardHeader>
              <div className="mx-auto bg-green-100 rounded-full p-3 w-fit mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-2xl text-green-800">Payment Successful!</CardTitle>
              <CardDescription className="text-lg">
                Your job has been assigned and your tradesman will contact you shortly
              </CardDescription>
            </CardHeader>
          </Card>

          {/* Payment Details */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Job Information */}
            <Card className="shadow-elegant">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  Job Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg">{paymentDetails.job?.title || "Job Title"}</h3>
                  <p className="text-muted-foreground">{paymentDetails.job?.description || "Job Description"}</p>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Location:</span>
                    <span className="font-medium">{paymentDetails.job?.location || "Location"}</span>
                  </div>
                  
                  {paymentDetails.job?.required_date && (
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Scheduled for:</span>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span className="font-medium">
                          {format(new Date(paymentDetails.job.required_date), "PPP 'at' p")}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Payment Breakdown */}
            <Card className="shadow-elegant">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  Payment Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between font-semibold text-base">
                    <span>Amount Paid:</span>
                    <span>£{paymentDetails.total_charge.toFixed(2)}</span>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded p-3 text-sm">
                  <p className="text-green-800 font-medium">✓ Payment Confirmed</p>
                  <p className="text-green-700">
                    Transaction completed successfully
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tradesman Information */}
          <Card className="shadow-elegant">
            <CardHeader>
              <CardTitle>Your Assigned Tradesman</CardTitle>
              <CardDescription>
                You can now contact them directly to arrange the work
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Name:</span>
                  <span className="font-medium">{paymentDetails.tradesman?.full_name || "Tradesman"}</span>
                </div>
                
                {paymentDetails.tradesman?.company_name && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Company:</span>
                    <span className="font-medium">{paymentDetails.tradesman.company_name}</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email:</span>
                  <span className="font-medium">{paymentDetails.tradesman?.email || "Email"}</span>
                </div>
                
                {paymentDetails.tradesman?.phone && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Phone:</span>
                    <span className="font-medium">{paymentDetails.tradesman.phone}</span>
                  </div>
                )}
              </div>
              
              {/* Contact Actions */}
              {paymentDetails.tradesman && (
                <div className="mt-4 flex gap-2">
                  {paymentDetails.tradesman.email && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      asChild
                    >
                      <a href={`mailto:${paymentDetails.tradesman.email}`}>
                        <Mail className="h-4 w-4 mr-2" />
                        Email
                      </a>
                    </Button>
                  )}
                  {paymentDetails.tradesman.phone && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      asChild
                    >
                      <a href={`tel:${paymentDetails.tradesman.phone}`}>
                        <Phone className="h-4 w-4 mr-2" />
                        Call
                      </a>
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Next Steps */}
          <Card className="shadow-elegant">
            <CardHeader>
              <CardTitle>What Happens Next?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <div className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                    1
                  </div>
                  <div>
                    <p className="font-medium">Contact Your Tradesman</p>
                    <p className="text-muted-foreground">
                      You can now contact your tradesman directly using the email and phone details above to discuss the work and arrange a suitable time.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                    2
                  </div>
                  <div>
                    <p className="font-medium">Work Completion</p>
                    <p className="text-muted-foreground">
                      The tradesman will complete the work as agreed and to the highest standards.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                    3
                  </div>
                  <div>
                    <p className="font-medium">Job Completion</p>
                    <p className="text-muted-foreground">
                      Once you're satisfied with the work, mark the job as complete in your dashboard.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-4 justify-center">
            <Button asChild variant="hero" size="lg">
              <Link to="/">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Return to Dashboard
              </Link>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};